import {DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule} from '@angular/core';
import {CurrencyPipe, DATE_PIPE_DEFAULT_OPTIONS, NgOptimizedImage, registerLocaleData} from "@angular/common";
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from './app-routing.module';
import { RouteReuseStrategy } from '@angular/router';
import { CustomRouteReuseStrategy } from './app-route-reuse-strategy';
import { AppComponent } from './app.component';

import { LibraryModule, CustomMessageService, COUNTRY_CODE_TOKEN, IS_EXPERANZA_FLOW_FN, UtilService } from "library";
import {environment} from "projects/store/src/environments/environment";
import { MacroFlowService } from "projects/store/src/app/services/api/v1/macro-flow.service";
import { HttpErrorInterceptor } from "projects/store/src/app/interceptors/http-error-interceptor.service";
import { ErrorLoggingService } from "projects/store/src/app/services/error-logging.service";

import localeCO from '@angular/common/locales/es-CO';
import localeEsPa from '@angular/common/locales/es-PA';
import localePE from '@angular/common/locales/es-PE';
import localeMX from '@angular/common/locales/es-MX';
import localeAR from '@angular/common/locales/es-AR';
registerLocaleData(localeCO);
registerLocaleData(localeEsPa, 'es-PA');
registerLocaleData(localePE);
registerLocaleData(localeMX);
registerLocaleData(localeAR);

// PrimeNG
import { MessageService } from "primeng/api";
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from "primeng/message";
import { ToastModule } from "primeng/toast";
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { CardModule } from "primeng/card";
import { SidebarModule } from "primeng/sidebar";
import { PanelMenuModule } from "primeng/panelmenu";
import { DialogModule } from "primeng/dialog";
import {TabMenuModule} from "primeng/tabmenu";
import {AvatarModule} from "primeng/avatar";
import {BadgeModule} from "primeng/badge";
import {BreadcrumbModule} from "primeng/breadcrumb";
import {AccordionModule} from "primeng/accordion";
import {TableModule} from "primeng/table";
import {CalendarModule} from "primeng/calendar";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {TabViewModule} from "primeng/tabview";
import {ChartModule} from "primeng/chart";
import {RippleModule} from "primeng/ripple";
import {SelectButtonModule} from "primeng/selectbutton";
import {SkeletonModule} from "primeng/skeleton";
import {DropdownModule} from "primeng/dropdown";
import { TagModule } from 'primeng/tag';
import {SliderModule} from 'primeng/slider';
import {RadioButtonModule} from "primeng/radiobutton";
import {InputMaskModule} from "primeng/inputmask";
import {TooltipModule} from "primeng/tooltip";
import {InputSwitchModule} from 'primeng/inputswitch';
import {CheckboxModule} from 'primeng/checkbox';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import {ListboxModule} from "primeng/listbox";
import {PanelModule} from "primeng/panel";
import {AutoCompleteModule} from "primeng/autocomplete";
import {InputNumberModule} from "primeng/inputnumber";
import {TriStateCheckboxModule} from "primeng/tristatecheckbox";
import { DataViewModule } from 'primeng/dataview';

// External libraries
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {QRCodeModule} from "angularx-qrcode";
import {WebcamModule} from 'ngx-webcam';

// Resources
import {CircleStoreIconComponent} from "projects/store/src/app/components/shared/resources/circle-store-icon/circle-store-icon.component";
import {CircleHomeIconComponent} from "projects/store/src/app/components/shared/resources/circle-home-icon/circle-home-icon.component";
import {CircleOnlineIconComponent} from "projects/store/src/app/components/shared/resources/circle-online-icon/circle-online-icon.component";
import {LogoKrediyaComponent} from "projects/store/src/app/components/shared/resources/logo-krediya/logo-krediya.component";
import {WhatsappIconComponent} from "projects/store/src/app/components/shared/resources/whatsapp-icon/whatsapp-icon.component";
import {LocationIconComponent} from "projects/store/src/app/components/shared/resources/location-icon/location-icon.component";


// Application
import { PermissionDirective} from "projects/store/src/app/directives/permission.directive";

import { AppLayoutComponent } from 'projects/store/src/app/components/layout/app.layout.component';
import { AppLayoutSalesComponent} from "projects/store/src/app/components/layout/app.layout-sales.component";
import { AppLayoutSalesV2Component } from 'projects/store/src/app/components/layout/app.layout-sales-v2.component';
import { AppBreadcrumbComponent } from 'projects/store/src/app/components/layout/app.breadcrumb.component';
import { AppHeaderComponent } from 'projects/store/src/app/components/layout/app.header.component';
import { AppProfileComponent } from 'projects/store/src/app/components/layout/app.profile.component';
import { AppSidebarComponent } from 'projects/store/src/app/components/layout/app.sidebar.component';
import { AppMenuComponent } from 'projects/store/src/app/components/layout/app.menu.component';
import { AppMenuitemComponent } from "projects/store/src/app/components/layout/app.menuitem.component";
import { AppLayoutCommissionsComponent } from 'projects/store/src/app/components/layout/app.layout-commissions.component';
import { AppLayoutReportsComponent } from 'projects/store/src/app/components/layout/app.layout-reports.component';

import { AuthComponent} from "projects/store/src/app/components/auth/auth.component";
import { LoginComponent} from "projects/store/src/app/components/auth/login/login.component";
import { LogoutComponent } from './components/auth/logout/logout.component';
import { ValidateUserComponent} from "projects/store/src/app/components/auth/validate-user/validate-user.component";
import { CheckUserComponent} from "projects/store/src/app/components/auth/check-user/check-user.component";
import { HomeComponent} from "projects/store/src/app/components/home/home.component";
import { CommissionDashboardComponent } from 'projects/store/src/app/components/commissions/dashboard/commission-dashboard.component';
import { CommissionListByBrandsComponent } from 'projects/store/src/app/components/commissions/list/by-brands/commission-list-by-brands.component';
import { CommissionPaymentsComponent } from 'projects/store/src/app/components/commissions/payments/commission-payments.component';
import { CommissionReportsComponent } from 'projects/store/src/app/components/commissions/reports/commission-reports.component';
import { CommissionsTableComponent} from "projects/store/src/app/components/shared/commissions-table/commissions-table.component";
import { CommissionStoresComponent } from 'projects/store/src/app/components/commissions/stores/commission-stores.component';
import { CommissionReportsSalesComponent } from 'projects/store/src/app/components/commissions/reports/sales/commission-reports-sales.component';
import { CommissionReportsCommissionsComponent } from 'projects/store/src/app/components/commissions/reports/commissions/commission-reports-commissions.component';
import { CommissionReportsPaymentsComponent } from 'projects/store/src/app/components/commissions/reports/payments/commission-reports-payments.component';
import { CommissionReportsCancellationsComponent } from 'projects/store/src/app/components/commissions/reports/cancellations/commission-reports-cancellations.component';
import { CustomMessageTabComponent } from './components/shared/custom-message-tab/custom-message-tab.component';
import { TabMenuComponent } from './components/shared/tab-menu/tab-menu.component';
import { SalesPreviewComponent } from './components/shared/sales-preview/sales-preview.component';
import { SalesComponent } from './components/sales/sales.component';
import { SalesQuotasInstallmentComponent } from 'projects/store/src/app/components/sales/quotas/installment/sales-quotas-installment.component';
import { SalesGatewayComponent } from 'projects/store/src/app/components/sales/gateway/sales-gateway.component';
import { SalesHeaderComponent } from 'projects/store/src/app/components/sales/general/header/sales-header.component';
import { SalesDocsPrivacyPolicyComponent } from './components/sales/general/docs/privacy-policy/sales-docs-privacy-policy.component';
import { SalesDocsCreditReportsComponent } from './components/sales/general/docs/credit-reports/sales-docs-credit-reports.component';
import { PageNotFoundComponent } from './components/general/page-not-found/page-not-found-component';
import { SellerTraceabilityReportComponent } from './components/reports/seller-traceability/seller-traceability-report.component';
import {DividerModule} from "primeng/divider";
import {MultiSelectModule} from "primeng/multiselect";
import {PaginatorModule} from "primeng/paginator";
import {CarouselModule} from "primeng/carousel";
import { SharedMessagesModule } from './components/shared/shared-messages/shared-messages.module';
import {SalesModule} from "./components/sales/sales.module";
import { AppLayoutIframeModule } from './components/layout/app.layout-iframe.module';
import { OneMarketerChatComponent } from './components/shared/one-marketer-chat/one-marketer-chat.component';
import {GeneralModule} from "./components/sales/general/general.module";
import { ClosingReportComponent } from './components/reports/closing-report/closing-report.component';
import { ClosingReportSummaryCardsComponent } from './components/reports/closing-report/components/closing-report-summary-cards/closing-report-summary-cards.component';
import { ClosingReportCreditsTableComponent } from './components/reports/closing-report/components/closing-report-credits-table/closing-report-credits-table.component';
import { ClosingReportCertificateComponent } from './components/reports/closing-report/components/closing-report-certificate/closing-report-certificate.component';

// New Version Flujo Universal
import { SalesStatusMessageComponentV3 } from 'projects/store/src/app/components/sales/status-message/sales-status-message-v3.component';
import { KrdyaLogoWhiteComponent } from './components/shared/resources/krdya-logo-white/krdya-logo-white.component';
import { AdditionalProductsSalesReportComponent } from './components/reports/additional-products-sales/additional-products-sales-report.component';


// New Version Kredicash
import { AppLayoutSalesV3Component } from 'projects/store/src/app/components/layout/app.layout-sales-v3.component';
import { SalesHeaderComponentV3 } from 'projects/store/src/app/components/sales/general/header/sales-header-v3.component';
import {SalesSimulatorKredicashComponentV3} from "projects/store/src/app/components/sales/simulator/kredicash-v3/sales-simulator-kredicash-v3.component";
import {
  SalesSimulatorKredicashV3VersionV2Component,
  SalesSimulatorKredicashV3VersionV3Component
} from "projects/store/src/app/components/sales/simulator/kredicash-v3/versions/sales-simulator-kredicash-v3-versions.component";
@NgModule({

  declarations: [
    AppComponent,
    AppLayoutComponent,
    AppLayoutSalesComponent,
    AppLayoutSalesV2Component,
    AppLayoutSalesV3Component,
    AppBreadcrumbComponent,
    AppHeaderComponent,
    AppProfileComponent,
    AppSidebarComponent,
    AppMenuComponent,
    AppLayoutCommissionsComponent,
    AppLayoutReportsComponent,
    AppMenuitemComponent,
    AuthComponent,
    LoginComponent,
    LogoutComponent,
    CheckUserComponent,
    ValidateUserComponent,
    HomeComponent,
    CommissionDashboardComponent,
    TabMenuComponent,
    CircleStoreIconComponent,
    CircleHomeIconComponent,
    CircleOnlineIconComponent,
    LocationIconComponent,
    LogoKrediyaComponent,
    WhatsappIconComponent,
    SalesPreviewComponent,
    CommissionListByBrandsComponent,
    CommissionPaymentsComponent,
    CommissionReportsComponent,
    CommissionsTableComponent,
    CommissionStoresComponent,
    CommissionReportsSalesComponent,
    CommissionReportsCommissionsComponent,
    CommissionReportsPaymentsComponent,
    CommissionReportsCancellationsComponent,
    CustomMessageTabComponent,
    PermissionDirective,
    SalesPreviewComponent,
    SalesComponent,
    SalesQuotasInstallmentComponent,
    SalesGatewayComponent,
    SalesHeaderComponent,
    SalesHeaderComponentV3,
    SalesSimulatorKredicashComponentV3,
    SalesSimulatorKredicashV3VersionV2Component,
    SalesSimulatorKredicashV3VersionV3Component,
    SalesStatusMessageComponentV3,
    SalesDocsPrivacyPolicyComponent,
    SalesDocsCreditReportsComponent,
    SellerTraceabilityReportComponent,
    PageNotFoundComponent,
    OneMarketerChatComponent,
    ClosingReportComponent,
    ClosingReportSummaryCardsComponent,
    ClosingReportCreditsTableComponent,
    ClosingReportCertificateComponent,
    KrdyaLogoWhiteComponent,
    AdditionalProductsSalesReportComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    LibraryModule,
    HttpClientModule,
    MessagesModule,
    MessageModule,
    ToastModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
    CardModule,
    SidebarModule,
    PanelMenuModule,
    DialogModule,
    NgOptimizedImage,
    TabMenuModule,
    AvatarModule,
    BadgeModule,
    BreadcrumbModule,
    AccordionModule,
    TableModule,
    CalendarModule,
    ConfirmDialogModule,
    TabViewModule,
    ChartModule,
    RippleModule,
    SelectButtonModule,
    DropdownModule,
    TagModule,
    SkeletonModule,
    SliderModule,
    FontAwesomeModule,
    LibraryModule,
    RadioButtonModule,
    InputMaskModule,
    InputSwitchModule,
    CheckboxModule,
    QRCodeModule,
    ScrollPanelModule,
    TooltipModule,
    ListboxModule,
    TagModule,
    PanelModule,
    AutoCompleteModule,
    InputNumberModule,
    TriStateCheckboxModule,
    DividerModule,
    MultiSelectModule,
    PaginatorModule,
    WebcamModule,
    CarouselModule,
    SalesModule,
    AppLayoutIframeModule,
    SharedMessagesModule,
    DataViewModule,
    GeneralModule,
  ],
  bootstrap: [AppComponent],
  exports: [],
  providers: [
    {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},
    CustomMessageService,
    MessageService,
    ErrorLoggingService,
    CurrencyPipe,
    {provide: DEFAULT_CURRENCY_CODE, useValue: environment.global.country.currency},
    {provide: LOCALE_ID, useValue: environment.global.country.locale},
    {provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: {timezone: environment.global.country.timezone}},
    {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
    {provide: COUNTRY_CODE_TOKEN, useValue: environment.global.country.code},
    {
      provide: IS_EXPERANZA_FLOW_FN,
      useFactory: (macroFlowService: MacroFlowService, utilService: UtilService) => {
        return () => macroFlowService.isExperanzaFlow(utilService.getFlowCodeByLocation());
      },
      deps: [MacroFlowService, UtilService]
    }
  ]
})
export class AppModule {
  constructor(errorLoggingService: ErrorLoggingService) {
    // Registrar globalmente para que CustomMessageService pueda acceder
    (window as any).__ErrorLoggingService = ErrorLoggingService;
  }
}
